import type { ReactNode } from 'react';
import { Column, Typography } from '@tempmee/react';
import { CenteredSpinner } from '~/commonComponents';
import type { IAuthorization } from '~/domain/Credentials/types/Credentials';
import { IJob, IMultipleCounters } from '~/models';
import { ShiftDetailsPanel } from '../../ShiftDetailsPanel';
import { CandidateListPanel } from './CandidateListPanel';
import { SingleCandidateView } from './SingleCandidateView';

export interface IConfirmCandidatesPrimaryProps {
  job: Partial<IJob> | null | undefined;
  counters: IMultipleCounters[];
  /** The candidate whose page is open, or `null` while the list is showing. */
  activeCounter: IMultipleCounters | null;
  authorizations?: IAuthorization[] | null;
  /** A read or a mutation is in flight. */
  isBusy: boolean;
  /** The candidates query has actually come back. */
  hasLoadedCandidates: boolean;
  isCollapsed: boolean;
  /** The page's single alert, already narrowed to one. */
  alert?: ReactNode;
  /** Confirm / Decline controls for the open candidate. */
  actions?: ReactNode;
  onSelectCandidate: (candidateId: string) => void;
  onBack: () => void;
}

/**
 * Which of the page's four primary states is showing: loading, nothing to show,
 * one candidate, or the list.
 *
 * Split out of the page so the branch order is one readable thing. It is an
 * order, not a set — the loading check has to come before the empty check, or a
 * page that has not fetched yet claims nobody applied.
 */
export const ConfirmCandidatesPrimary = ({
  job,
  counters,
  activeCounter,
  authorizations,
  isBusy,
  hasLoadedCandidates,
  isCollapsed,
  alert,
  actions,
  onSelectCandidate,
  onBack,
}: IConfirmCandidatesPrimaryProps) => {
  if (isBusy) {
    return <CenteredSpinner minHeight={400} />;
  }

  // An empty list is two different situations, and the office needs to know
  // which: nobody has applied, or the list could not be read. Guarding the first
  // message on the query having actually succeeded is what keeps the page from
  // reporting a failed fetch as "nobody applied".
  if (counters.length === 0) {
    return (
      <ShiftDetailsPanel title="Candidates" data-testid="candidate-list-empty">
        <Typography variant="body1" as="p" className="text-text-secondary">
          {hasLoadedCandidates
            ? 'No professionals have applied to this shift yet.'
            : 'We could not load the candidates for this shift. Please refresh to try again.'}
        </Typography>
      </ShiftDetailsPanel>
    );
  }

  if (activeCounter) {
    return (
      <SingleCandidateView
        job={job}
        counter={activeCounter}
        authorizations={authorizations}
        alert={alert}
        isCollapsed={isCollapsed}
        onBack={onBack}
        actions={actions}
      />
    );
  }

  return (
    <Column gap={4} className="w-full">
      {alert}
      <CandidateListPanel counters={counters} job={job} onSelectCandidate={onSelectCandidate} />
    </Column>
  );
};
